import {Button, Group, Modal, Stack, Switch, TextInput} from '@mantine/core'
import {type GetInputPropsReturnType, useForm} from '@mantine/form'
import {useEffect, useState} from 'react'

import type {CreateWorkerTypeInput, WorkerType} from './schemas'

type WorkerTypeFormValues = {
    name: string
    isDriver: boolean
    isTechnician: boolean
}

const EMPTY_VALUES: WorkerTypeFormValues = {name: '', isDriver: false, isTechnician: false}

export function WorkerTypeRoleSwitches({
    isDriver,
    isTechnician,
}: {
    isDriver: GetInputPropsReturnType
    isTechnician: GetInputPropsReturnType
}) {
    return (
        <>
            <Switch label="Fills driver slots" description="Requires a home yard" {...isDriver} />
            <Switch
                label="Field technician"
                description="Assignable without a driver"
                {...isTechnician}
            />
        </>
    )
}

type WorkerTypeFormModalProps = {
    opened: boolean
    onClose: () => void
    onCreate: (input: CreateWorkerTypeInput) => Promise<WorkerType>
    onSaved: () => void
}

export function WorkerTypeFormModal({
    opened,
    onClose,
    onCreate,
    onSaved,
}: WorkerTypeFormModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false)

    const form = useForm<WorkerTypeFormValues>({
        initialValues: EMPTY_VALUES,
        validate: {
            name: (value) => (value.trim() ? null : 'Type name is required'),
        },
    })

    useEffect(() => {
        if (!opened) {
            return
        }
        form.setValues(EMPTY_VALUES)
        form.clearErrors()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset modal fields when opened
    }, [opened])

    function handleClose() {
        form.reset()
        onClose()
    }

    async function handleSubmit(values: WorkerTypeFormValues) {
        setIsSubmitting(true)
        try {
            await onCreate({
                name: values.name.trim(),
                isDriver: values.isDriver,
                isTechnician: values.isTechnician,
            })
            onSaved()
            form.reset()
            onClose()
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Modal opened={opened} onClose={handleClose} title="Add worker type" centered>
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                    <TextInput label="Name" withAsterisk {...form.getInputProps('name')} />
                    <WorkerTypeRoleSwitches
                        isDriver={form.getInputProps('isDriver', {type: 'checkbox'})}
                        isTechnician={form.getInputProps('isTechnician', {type: 'checkbox'})}
                    />

                    <Group justify="flex-end">
                        <Button type="button" variant="default" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={isSubmitting}>
                            Create type
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    )
}
